import { MCPToolName } from "@qw/qw-common";
import {
  Button,
  CodeBlock,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
  Icon,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@qw/ui";
import { type FC, useCallback, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { getToolMessagePartName, isToolMessagePart } from "../chat.helpers";
import type { ExtendedUIMessage, MessagePartToolInvocation } from "../chat.types";
import { useChat } from "../providers/chat-provider";
import { useToolDescription } from "../providers/chat-tool-metadata-provider";
import { MarkdownComponents } from "../utils/markdown-components";
import {
  ChatMessageToolInvocation,
  ChatMessageToolInvocationContent,
  ChatMessageToolInvocationHeader,
  ChatMessageToolInvocationTitle,
  ChatMessageToolInvocationToggleButton,
} from "./chat-message-tool-invocation";
import { ChatSessionMessageResourceLinks } from "./chat-session-message-resource-links";
import { ComputeBrokerFeeToolInvocationResult } from "./compute-broker-fee-tool-invocation-result";
import { ElicitFormRenderer } from "./elicit-form-renderer";
import {
  getElicitationDisplayState,
  getElicitationFieldDisplayValue,
  getElicitationFieldLabel,
} from "./elicitation-display";

export interface ChatSessionMessageToolInvocationProps {
  toolInvocation: MessagePartToolInvocation;
  message?: ExtendedUIMessage;
}

export const ChatSessionMessageToolInvocation = ({
  toolInvocation,
  message,
}: ChatSessionMessageToolInvocationProps) => {
  const toolDescription = useToolDescription(getToolMessagePartName(toolInvocation));

  switch (toolInvocation.state) {
    case "input-streaming":
      return (
        <PartialCallChatSessionMessageToolInvocation
          toolInvocation={toolInvocation}
          toolDescription={toolDescription}
        />
      );

    case "input-available":
      return <CallChatSessionMessageToolInvocation toolInvocation={toolInvocation} toolDescription={toolDescription} />;

    case "output-available":
    case "output-error":
      // Both success and error states are rendered similarly - the ResultChatSessionMessageToolInvocation
      // component handles error display via the hasError check on the output content
      return (
        <ResultChatSessionMessageToolInvocation
          toolInvocation={toolInvocation}
          toolDescription={toolDescription}
          message={message}
        />
      );

    default:
      return null;
  }
};

export interface CallChatSessionMessageToolInvocationProps extends ChatSessionMessageToolInvocationProps {
  toolInvocation: MessagePartToolInvocation;
  toolDescription: string;
}

export const CallChatSessionMessageToolInvocation = ({
  toolInvocation,
  toolDescription,
}: CallChatSessionMessageToolInvocationProps) => {
  const toolName = getToolMessagePartName(toolInvocation);

  return (
    <ChatMessageToolInvocation>
      <ChatMessageToolInvocationHeader>
        <Icon icon="build" className="text-primary" />
        <ChatMessageToolInvocationTitle tooltip={toolName}>
          Using tool: {toolDescription}
        </ChatMessageToolInvocationTitle>
        <ChatMessageToolInvocationToggleButton />
      </ChatMessageToolInvocationHeader>
      <ChatMessageToolInvocationContent content={toolInvocation.input} />
    </ChatMessageToolInvocation>
  );
};

export interface PartialCallChatSessionMessageToolInvocationProps extends ChatSessionMessageToolInvocationProps {
  toolInvocation: MessagePartToolInvocation;
  toolDescription: string;
}

export const PartialCallChatSessionMessageToolInvocation = ({
  toolInvocation,
  toolDescription,
}: PartialCallChatSessionMessageToolInvocationProps) => {
  const toolName = getToolMessagePartName(toolInvocation);

  return (
    <ChatMessageToolInvocation>
      <ChatMessageToolInvocationHeader>
        <Icon icon="pending" className="text-muted-foreground" />
        <ChatMessageToolInvocationTitle tooltip={toolName}>
          Preparing tool: {toolDescription}
        </ChatMessageToolInvocationTitle>
      </ChatMessageToolInvocationHeader>
    </ChatMessageToolInvocation>
  );
};

export interface ResultChatSessionMessageToolInvocationProps extends ChatSessionMessageToolInvocationProps {
  toolInvocation: MessagePartToolInvocation;
  toolDescription: string;
  message?: ExtendedUIMessage;
}

export const ResultChatSessionMessageToolInvocation = ({
  toolInvocation,
  toolDescription,
  message,
}: ResultChatSessionMessageToolInvocationProps) => {
  const toolName = getToolMessagePartName(toolInvocation) as MCPToolName;

  if (toolName === MCPToolName.Elicit) {
    return (
      <ElicitToolInvocationResult toolInvocation={toolInvocation} toolDescription={toolDescription} message={message} />
    );
  }

  if (toolName === MCPToolName.ComputeBrokerFee) {
    const output = parseJson(toolInvocation.output);
    const hasError = output?.isError || output?.error || toolInvocation.errorText;
    if (!hasError) {
      // The MCP server wraps tool outputs as structuredContent: { result: { status, data } }
      // (see quotewell-mcp-server.service.ts). Unwrap to the tool's own structuredContent.
      const wrapped = output?.structuredContent;
      const worksheetCandidate =
        wrapped && typeof wrapped === "object" && "result" in wrapped
          ? (wrapped as { result?: { data?: unknown } }).result?.data
          : wrapped;
      return (
        <ComputeBrokerFeeToolInvocationResult
          structuredContent={worksheetCandidate}
          toolDescription={toolDescription}
          toolName={toolName}
        />
      );
    }
    // Fall through to DefaultToolInvocationResult on error
  }

  return (
    <DefaultToolInvocationResult toolInvocation={toolInvocation} toolDescription={toolDescription} message={message} />
  );
};

export const DefaultToolInvocationResult = ({
  toolInvocation,
  toolDescription,
}: ResultChatSessionMessageToolInvocationProps) => {
  const toolName = getToolMessagePartName(toolInvocation) as MCPToolName;

  const { chatSession } = useChat();
  const chatSessionId = chatSession?.id;

  const content = useMemo(() => {
    const input = parseJson(toolInvocation.input);
    const output = parseJson(toolInvocation.output);
    const hasError = output?.isError || output?.error || toolInvocation.errorText;

    return { input, output, hasError };
  }, [toolInvocation]);

  const structuredContent = useMemo(() => content.output?.structuredContent ?? {}, [content.output]);

  return (
    <div className="w-full max-w-full flex flex-col text-muted-foreground">
      <ChatMessageToolInvocation className={cn(content.hasError && "text-destructive")}>
        <ChatMessageToolInvocationHeader>
          <Tooltip>
            <TooltipTrigger className="mt-[4px]">
              <Icon
                icon={content.hasError ? "error" : "check"}
                className={cn(content.hasError && "text-destructive")}
              />
            </TooltipTrigger>
            <TooltipContent>{content.hasError ? "Failed" : "Success"}</TooltipContent>
          </Tooltip>
          <ChatMessageToolInvocationTitle tooltip={toolName}>{toolDescription}</ChatMessageToolInvocationTitle>
          <ChatMessageToolInvocationToggleButton />
        </ChatMessageToolInvocationHeader>

        <ChatMessageToolInvocationContent content={content}>
          {!content.hasError && (
            <ChatSessionMessageToolInvocationStructuredContent structuredContent={structuredContent} />
          )}
        </ChatMessageToolInvocationContent>
      </ChatMessageToolInvocation>

      {!content.hasError && (
        <ChatSessionMessageResourceLinks toolInvocationOutput={content.output} chatSessionId={chatSessionId} />
      )}
    </div>
  );
};

export const ElicitToolInvocationResult = ({
  toolInvocation,
  message,
}: ResultChatSessionMessageToolInvocationProps) => {
  const { isReadOnly, messages } = useChat();

  // Declaratively detect if this elicitation was resolved but the tool output wasn't updated
  // in the AI SDK's in-memory state. This happens because:
  // - Path A (form submit/skip): the resolve endpoint doesn't update the original tool output
  // - Path B (chat message): the user typed a message instead of using the form
  //
  // We detect this by checking if the conversation continued past this tool invocation:
  // 1. Any message (user or assistant) appears after the containing message, OR
  // 2. The containing message has content parts after this tool invocation (text, other tools)
  const isImplicitlyResolved = useMemo(() => {
    if (!message?.id) {
      return false;
    }
    const msgIndex = messages.findIndex((m) => m.id === message.id);
    if (msgIndex === -1) {
      return false;
    }

    // Check if any messages follow this one
    if (msgIndex < messages.length - 1) {
      return true;
    }

    // Check if the containing message has parts after this tool invocation
    const parts = message.parts ?? [];
    const thisPartIndex = parts.findIndex(
      (p: any) => isToolMessagePart(p) && p.toolCallId === toolInvocation.toolCallId
    );
    if (thisPartIndex !== -1 && thisPartIndex < parts.length - 1) {
      // There are parts after this one — conversation continued
      return parts.slice(thisPartIndex + 1).some((p: any) => p.type === "text" || isToolMessagePart(p));
    }

    return false;
  }, [messages, message?.id, message?.parts, toolInvocation.toolCallId]);

  const content = useMemo(() => {
    const input = parseJson(toolInvocation.input);
    const output = parseJson(toolInvocation.output);
    const hasError = output?.isError || output?.error || toolInvocation.errorText;

    const errorText =
      toolInvocation.errorText ||
      (typeof output?.text === "string" ? output.text : null) ||
      (typeof output?.error === "string" ? output.error : null) ||
      (hasError ? "An error occurred" : null);

    // Detect structured vs legacy shape
    const isStructured = input?.requestedSchema !== undefined && input?.requestedSchema !== null;

    // Legacy: questions array
    const questions: string[] = Array.isArray(input?.questions) ? input.questions : [];
    // Structured: message + requestedSchema
    const elicitMessage: string | undefined = typeof input?.message === "string" ? input.message : undefined;
    const requestedSchema = isStructured ? input.requestedSchema : undefined;

    // Parse response (output) for Q&A display
    const elicitResponse = !hasError && output?.action ? output : undefined;

    return { questions, elicitMessage, requestedSchema, isStructured, hasError, errorText, elicitResponse };
  }, [toolInvocation]);

  // Validation errors are LLM-facing retries (e.g., message too long, invalid schema).
  // Show a minimal collapsed indicator — expandable for debugging.
  if (content.hasError) {
    return (
      <Collapsible className="my-1">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" theme="destructive" size="xs" className="group text-xs -mx-1.5 -mb-1">
            <Icon icon="error" />
            <span>Validation error</span>
            <Icon icon="keyboard_arrow_down" className="transition-transform group-data-[state=open]:rotate-180" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-1">
          <pre className="text-2xs text-muted-foreground whitespace-pre-wrap break-all">{content.errorText}</pre>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  // Structured elicitation (new shape)
  if (content.isStructured && content.elicitMessage) {
    const isResolved = !!content.elicitResponse;

    if (isResolved) {
      return <ElicitQADisplay input={toolInvocation.input} output={toolInvocation.output} />;
    }

    // If the conversation continued past this elicitation, it was resolved (skipped/declined/answered).
    // Check the optimistic cache for the actual response; fall back to "cancel" if not found.
    if (isImplicitlyResolved) {
      const cachedResponse = optimisticElicitResponses.get(toolInvocation.toolCallId);
      return (
        <ElicitQADisplay
          input={{ message: content.elicitMessage, requestedSchema: content.requestedSchema }}
          output={cachedResponse ?? { action: "cancel" }}
        />
      );
    }

    if (isReadOnly && content.requestedSchema) {
      return <ElicitReadOnlyState message={content.elicitMessage} />;
    }

    // Pending: show interactive form
    if (content.requestedSchema) {
      return (
        <ElicitFormWrapper
          toolCallId={toolInvocation.toolCallId}
          message={content.elicitMessage}
          requestedSchema={content.requestedSchema}
        />
      );
    }

    return <div className="text-sm text-muted-foreground">{content.elicitMessage}</div>;
  }

  // Legacy: plain-text questions
  if (content.questions.length === 0) {
    return <div className="text-destructive">No question provided</div>;
  }

  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown components={MarkdownComponents} remarkPlugins={[remarkGfm]}>
        {content.questions.join("\n\n")}
      </ReactMarkdown>
    </div>
  );
};

/**
 * Module-level cache of optimistic elicitation responses, keyed by toolCallId.
 * Survives component remounts caused by AI SDK message list updates during streaming.
 * Entries are cleared when the component unmounts after the server state catches up (page reload).
 */
const optimisticElicitResponses = new Map<string, Record<string, unknown>>();

const ElicitFormWrapper = ({
  toolCallId,
  message,
  requestedSchema,
}: {
  toolCallId: string;
  message: string;
  requestedSchema: any;
}) => {
  const { submitElicitationResponse, isElicitationSubmitting } = useChat();
  const [submittedResponse, setSubmittedResponse] = useState<Record<string, unknown> | null>(
    () => optimisticElicitResponses.get(toolCallId) ?? null
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleElicitAction = useCallback(
    async (tcId: string, action: "accept" | "decline" | "cancel", content?: Record<string, unknown>) => {
      const response = content ? { action, content } : { action };
      optimisticElicitResponses.set(tcId, response);
      setSubmittedResponse(response);
      try {
        await submitElicitationResponse(tcId, response);
      } catch (error) {
        optimisticElicitResponses.delete(tcId);
        setSubmittedResponse(null);
        const label = action === "accept" ? "submit" : action === "decline" ? "decline" : "skip";
        setSubmitError(error instanceof Error ? error.message : `Failed to ${label}`);
      }
    },
    [submitElicitationResponse]
  );

  const handleSubmit = useCallback(
    (tcId: string, content: Record<string, unknown>) => handleElicitAction(tcId, "accept", content),
    [handleElicitAction]
  );

  const handleDecline = useCallback((tcId: string) => handleElicitAction(tcId, "decline"), [handleElicitAction]);

  const handleCancel = useCallback((tcId: string) => handleElicitAction(tcId, "cancel"), [handleElicitAction]);

  if (submittedResponse) {
    return <ElicitQADisplay input={{ message, requestedSchema }} output={submittedResponse} />;
  }

  return (
    <ElicitFormRenderer
      toolCallId={toolCallId}
      message={message}
      requestedSchema={requestedSchema}
      onSubmit={handleSubmit}
      onDecline={handleDecline}
      onCancel={handleCancel}
      disabled={isElicitationSubmitting}
      error={submitError}
    />
  );
};

const ElicitReadOnlyState = ({ message }: { message: string }) => (
  <div className="border border-input rounded-lg bg-popover mt-2 overflow-hidden -mx-2 2xl:-mx-1">
    <div className="flex items-center gap-1.5 border-b border-input px-3 py-2 text-xs text-muted-foreground">
      <Icon icon="visibility" className="text-muted-foreground" />
      <span className="font-medium">Read-only request</span>
    </div>

    <div className="space-y-2 p-3">
      <p className="text-sm text-foreground">{message}</p>
      <p className="text-sm text-muted-foreground">Only the session owner can respond to this request from Alby.</p>
    </div>
  </div>
);

/**
 * Q&A display: shows the structured question/answer pairs after elicitation is resolved,
 * rendered inside a card that matches the form's visual style.
 */
const ElicitQADisplay = ({ input, output }: { input: any; output: any }) => {
  const parsed = useMemo(() => {
    const parsedInput = parseJson(input);
    const parsedOutput = parseJson(output);
    const schema = parsedInput?.requestedSchema;
    const message: string | undefined = typeof parsedInput?.message === "string" ? parsedInput.message : undefined;
    const response = parsedOutput;

    if (!schema?.properties || !response) {
      return null;
    }

    return { schema, message, response };
  }, [input, output]);

  if (!parsed) {
    return null;
  }

  const { schema, message, response } = parsed;

  // Accept: build key-value entries
  const entries = Object.entries(schema.properties || {});
  const content = response.content || {};
  const displayState = getElicitationDisplayState(schema, response);

  return (
    <div className="border border-input rounded-lg bg-popover mt-2 overflow-hidden -mx-2 2xl:-mx-1">
      <div className="flex items-center gap-1.5 border-b border-input py-2 px-3 text-xs text-muted-foreground">
        <Icon icon={displayState.icon} className="text-muted-foreground" />
        <span className="font-medium">{displayState.headerLabel}</span>
      </div>

      <div className="p-3 space-y-3">
        {message && <p className="text-sm text-foreground">{message}</p>}

        {!displayState.showEntries ? (
          <p className="text-sm text-muted-foreground italic flex items-center gap-1">
            <Icon icon="block" className="text-muted-foreground" />
            {displayState.statusText}
          </p>
        ) : (
          <div className="grid gap-3">
            {entries.map(([key, prop]: [string, any]) => {
              const value = content[key];
              const label = getElicitationFieldLabel(key, prop.title, displayState.isConfirmation);
              const displayValue =
                getElicitationFieldDisplayValue(key, value, displayState.isConfirmation) ??
                formatDisplayValue(value, prop);

              return (
                <div key={key} className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-muted-foreground">{label}</span>
                  <span className="text-sm text-foreground">{displayValue}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

function formatDisplayValue(value: unknown, prop: any): string {
  if (value === undefined || value === null) {
    return "\u2014";
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "\u2014";
    }
    return value
      .map((v: string) => {
        const opt = prop.items?.anyOf?.find((o: any) => o.const === v);
        return opt?.title ?? v;
      })
      .join(", ");
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (prop.oneOf) {
    return prop.oneOf.find((o: any) => o.const === value)?.title ?? String(value);
  }

  return String(value);
}

const ChatSessionMessageToolInvocationStructuredContent: FC<{ structuredContent: any }> = ({ structuredContent }) => (
  <Sheet>
    <SheetTrigger asChild>
      <Button size="xs" variant="outline" className="max-w-full justify-start mb-2">
        <Icon icon="file_json" className="text-muted-foreground" />
        <span className="truncate">structured-content.json</span>
      </Button>
    </SheetTrigger>
    <SheetContent className="sm:w-3/4 lg:w-3/5 sm:max-w-full">
      <SheetHeader>
        <SheetTitle>Structured Content</SheetTitle>
      </SheetHeader>
      <CodeBlock>{JSON.stringify(structuredContent, null, 2)}</CodeBlock>
    </SheetContent>
  </Sheet>
);

function parseJson(json: any) {
  if (typeof json !== "string") {
    return json;
  }

  try {
    return JSON.parse(json);
  } catch {
    return json;
  }
}
